import { useEffect } from "react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { get, isString } from "radash"

import type { Invoice, Userdata } from "@be-miniapp/api"
import { useTranslation } from "@be-miniapp/i18n"
import type { Location } from "@be-miniapp/types"
import { toast } from "@be-miniapp/ui/hooks/use-toast"

export const beUserQueryKeys = {
  all: ["be-user"],
  user: (accessToken: string, location: Location | undefined) => [
    ...beUserQueryKeys.all,
    accessToken,
    location,
  ],
}

interface BeUserDataState {
  data: Userdata | undefined
  accessToken: string | null
  invoice: Invoice | undefined
  setInvoice: (invoice?: Invoice) => void
}

type BeUserDataSelector<T> = (state: BeUserDataState) => T

type SelectorMode = "return" | "side_effect"

interface CreateUseBeUserDataParams<
  TSelectorMode extends SelectorMode = "return",
> {
  loginByAccessToken: () => Promise<{
    data: { flag: number; user_data?: Userdata }
  }>
  selectorMode?: TSelectorMode
}

interface UseBeUserDataParams {
  location: Location | undefined
  accessToken: string | null
  invoice: Invoice | undefined
  setInvoice: (invoice?: Invoice) => void
  onSessionExpired: () => void
}

type UseBeUserDataSelectorReturn<
  TSelectorMode extends SelectorMode,
  T,
> = TSelectorMode extends "side_effect" ? null : T

interface UseBeUserDataHook<TSelectorMode extends SelectorMode> {
  (params: UseBeUserDataParams): BeUserDataState
  <T>(
    params: UseBeUserDataParams,
    selector: BeUserDataSelector<T>
  ): UseBeUserDataSelectorReturn<TSelectorMode, T>
}

function createUseBeUserData<TSelectorMode extends SelectorMode = "return">({
  loginByAccessToken,
  selectorMode,
}: CreateUseBeUserDataParams<TSelectorMode>): UseBeUserDataHook<TSelectorMode> {
  const mode: SelectorMode = selectorMode ?? "return"

  function useBeUserData<T>(
    params: UseBeUserDataParams,
    selector?: BeUserDataSelector<T>
  ): T | BeUserDataState | null {
    const { location, accessToken, invoice, onSessionExpired, setInvoice } =
      params
    const { t } = useTranslation()

    const { data } = useQuery({
      queryKey: beUserQueryKeys.user(accessToken || "", location),
      queryFn: async (): Promise<Userdata> => {
        const resp = await loginByAccessToken()

        if (resp.data.flag === 101) {
          toast({
            title: t("session_expired_title"),
            description: t("session_expired_description"),
          })
          onSessionExpired()
        }

        return resp.data.user_data as Userdata
      },
      enabled: isString(accessToken),
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: false,
      placeholderData: keepPreviousData,
      staleTime: 30 * 24 * 60 * 60 * 1000,
    })

    useEffect(() => {
      if (!data) return
      if (get(invoice, "updated_at")) return

      const fromApiInvoice = get(data, "invoices[0]", null)
      if (fromApiInvoice) setInvoice(fromApiInvoice)
    }, [data, invoice, setInvoice])

    const state: BeUserDataState = {
      data,
      accessToken,
      invoice,
      setInvoice,
    }

    if (!selector) return state

    const selected = selector(state)
    if (mode === "side_effect") return null
    return selected
  }

  return useBeUserData as UseBeUserDataHook<TSelectorMode>
}

export { createUseBeUserData }
export type { BeUserDataSelector, BeUserDataState, UseBeUserDataParams }
